import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Search, Filter } from 'lucide-react';
import axios from 'axios';
import BackButton from '@/components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blogs = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedYear, setSelectedYear] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, searchQuery, selectedYear]);

  const fetchBlogs = async () => {
    try {
      const response = await axios.get(`${API}/blogs?sort_by=created_at`);
      setBlogs(response.data);
      
      // Extract unique years from blog articles
      const years = [...new Set(response.data.map(article => {
        const year = new Date(article.date).getFullYear();
        return year;
      }))].sort((a, b) => b - a);
      
      setAvailableYears(years);
    } catch (error) {
      console.error('Error fetching blogs:', error);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (article.short_content && article.short_content.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // Filter by year
    if (selectedYear !== 'all') {
      filtered = filtered.filter(article => {
        const articleYear = new Date(article.date).getFullYear();
        return articleYear === parseInt(selectedYear);
      });
    }

    setFilteredBlogs(filtered);
  };

  return (
    <div className="blogs-page">
      {/* Hero Section with Background Image */}
      <section 
        className="relative py-32 pt-40 text-white"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1636275133457-b57780aeeab0?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/65 to-black/75"></div>
        
        {/* Back Button */}
        <div className="absolute top-24 left-6 z-10">
          <BackButton />
        </div>

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-in">Blogs & Articles</h1>
          <p className="text-lg sm:text-xl max-w-3xl text-gray-200 animate-in" style={{ animationDelay: '0.2s' }}>
            Explore our latest insights, thoughts, and stories from our community.
          </p>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="py-8 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search blog articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent transition-all duration-200"
              />
            </div>

            {/* Year Filter */}
            <div className="relative w-full md:w-48">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#20B2AA] focus:border-transparent transition-all duration-200 appearance-none cursor-pointer bg-white"
              >
                <option value="all">All Years</option>
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Results Count */}
            <div className="text-sm text-gray-600 whitespace-nowrap">
              {filteredBlogs.length} {filteredBlogs.length === 1 ? 'article' : 'articles'}
            </div>
          </div>

          {/* Active Filters */}
          {(searchQuery || selectedYear !== 'all') && (
            <div className="mt-4 flex flex-wrap gap-2">
              {searchQuery && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#20B2AA]/10 text-[#20B2AA] rounded-full text-sm">
                  <span>Search: "{searchQuery}"</span>
                  <button onClick={() => setSearchQuery('')} className="hover:text-[#189a93]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              {selectedYear !== 'all' && (
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#20B2AA]/10 text-[#20B2AA] rounded-full text-sm">
                  <span>Year: {selectedYear}</span>
                  <button onClick={() => setSelectedYear('all')} className="hover:text-[#189a93]">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}
              <button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedYear('all');
                }}
                className="text-sm text-gray-600 hover:text-[#20B2AA] transition-colors duration-200"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Blogs Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">
                {blogs.length === 0 
                  ? 'No blog articles available at the moment.' 
                  : 'No articles match your search criteria. Try adjusting your filters.'}
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((article, index) => (
                <Link
                  to={`/blogs/${article.slug || article.id}`}
                  key={article.id}
                  data-testid={`blog-article-${index}`}
                >
                  <article
                    className="group bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer scale-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                      <img
                        src={article.image_url || 'https://images.unsplash.com/photo-1559690869-1005b5a5ee41?crop=entropy&cs=srgb&fm=jpg&q=85'}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                        <Calendar className="h-4 w-4" />
                        <span>{article.date}</span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3 text-black group-hover:text-[#20B2AA] transition-colors duration-200">
                        {article.title}
                      </h3>
                      <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                        {article.short_content || article.content}
                      </p>
                      <div className="flex items-center gap-2 text-[#20B2AA] font-medium text-sm group-hover:gap-4 transition-all duration-200">
                        Read More
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blogs;
